import React, {useMemo} from 'react';

import {BulkActionDownload} from 'src/components/bulk-tool';
import {
    useLicenseDataModules,
    useLicenseFeatureModules,
    useLicenseFeatures,
    useLicenseTypes,
} from 'src/hooks/dictionaries';

import {Dropdown} from '@pb/kit-component-dropdown';
import {Group} from '@pb/kit-component-group';
import {Icon, Icons} from '@pb/kit-component-icon';
import Label from '@pb/kit-component-label';

import {createMetainfoString} from './utils';

import type {Features} from './typedef';
import type {BulkActionResult} from 'src/components/bulk-tool/typedef';
import type {UpdateLicenseResultItem} from 'src/openapi/admin/models/UpdateLicenseResultItem';


const PLUGIN_AND_APPS = ['EXCEL_PLUGIN', 'API', 'CHROME_EXTENSION'];

type Props = {
    toolTitle: string,
    fileName: string,
    fileHeaders: Record<string, string>,
    bulkResult: BulkActionResult<UpdateLicenseResultItem, Features>,
};

export const ResultModalDescription = ({toolTitle, fileName, fileHeaders, bulkResult}: Props) => {
    const {getLicenseTypeByCode} = useLicenseTypes();
    const {licenseDataModules} = useLicenseDataModules();
    const {licenseFeaturesModules} = useLicenseFeatureModules();
    const {licenseFeatures} = useLicenseFeatures();

    const dataModules = useMemo(() => {
        return bulkResult.additionalData?.dataModules.map(module => {
            const [parentId, childId] = module.split(':');
            const parent = licenseDataModules.find(item => item.id === parentId);
            const child = parent?.children?.find(item => item.id === childId);
            return `${parent?.description} (${child?.description})`;
        }) || [];
    }, [licenseDataModules, bulkResult]);

    const platformFeatures = useMemo(() => {
        return bulkResult.additionalData?.featureModules.map(module => {
            const parent = licenseFeaturesModules.find(item => item.code === module);
            return `${parent?.description}`;
        }) || [];
    }, [licenseFeaturesModules, bulkResult]);

    const exportOptions = useMemo(() => {
        return bulkResult.additionalData?.availableFeatures
            .filter(feature => !PLUGIN_AND_APPS.includes(feature))
            .map(module => {
                const parent = licenseFeatures.find(item => item.code === module);
                return `${parent?.description}`;
            }) || [];
    }, [licenseFeatures, bulkResult]);

    const pluginsAndApps = useMemo(() => {
        return bulkResult.additionalData?.availableFeatures
            .filter(feature => PLUGIN_AND_APPS.includes(feature))
            .map(module => {
                const parent = licenseFeatures.find(item => item.code === module);
                return `${parent?.description}`;
            }) || [];
    }, [licenseFeatures, bulkResult]);

    const downloadMeta = useMemo(() => {
        const exportOptionsMetainfo = createMetainfoString(exportOptions, 'Export Options');
        const updatedDataModulesMetainfo = createMetainfoString(dataModules, 'Standard Data Set');
        const updatedFeatureModulesMetainfo = createMetainfoString(platformFeatures, 'Platform Feature');
        const pluginsAndAppsMetainfo = createMetainfoString(pluginsAndApps, 'Plugins and Apps');

        return `${toolTitle},,,,\n` +
            `${updatedDataModulesMetainfo}` +
            `${updatedFeatureModulesMetainfo}` +
            `${pluginsAndAppsMetainfo}` +
            `${exportOptionsMetainfo}`;
    }, [toolTitle, exportOptions, dataModules, platformFeatures, pluginsAndApps]);


    return (
        <Group justify="space-between" alignItems="center">
            <Group gap="xs" alignItems="center">
                <b>Update results</b>
                <Dropdown
                    placement="right"
                    hoverEnabled={true}
                    anchor={({anchorRef, getAnchorProps}) => (
                        <span ref={anchorRef} {...getAnchorProps()}>
                            <Icon type={Icons.INFO}/>
                        </span>
                    )}>
                    <div>
                        <Label>Module Access Permissions</Label>
                        <div style={{maxWidth: 900}}>
                            <Group direction="v" alignItems="start">
                                {!!dataModules.length &&
                                    <div><strong>Standard Data Set:</strong> {dataModules.join(', ')}</div>}
                                {!!platformFeatures.length &&
                                    <div><strong>Platform Features:</strong> {platformFeatures.join(', ')}</div>}
                                {!!pluginsAndApps.length &&
                                    <div><strong>Plugins and Apps:</strong> {pluginsAndApps.join(', ')}</div>}
                                {!!exportOptions.length &&
                                    <div><strong>Export Options:</strong> {exportOptions.join(', ')}</div>}
                            </Group>
                        </div>
                    </div>
                </Dropdown>
            </Group>
            <BulkActionDownload
                toolName={toolTitle}
                fileName={fileName}
                headers={fileHeaders}
                startTime={bulkResult.startTime}
                meta={downloadMeta}
                body={bulkResult.itemList?.map(item => ({
                    ...item,
                    accountId: item.accountId?.toString(),
                    licenseId: item.licenseId?.toString(),
                    licenseType: getLicenseTypeByCode(item.licenseType),
                    actionStatus: item.status,
                    errors: item.errors?.join('. '),
                })) as Record<string, string>[]}
            />
        </Group>
    );
};
